/**
 * What the table toolbar turns into while rows are picked.
 *
 * The count on the left is replaced, not joined: `Selected:` and a badge with
 * the number, an X that lets the whole selection go, and then the actions that
 * apply to it. The search and the filters step aside while this shows, because
 * a bulk action acts on the rows picked and not on whatever the search
 * currently finds, and showing both invites reading one as the other.
 *
 * Destructive actions are passed as `danger-ghost` buttons by the caller. This
 * bar does not decide what is destructive, it only lays the actions out.
 */
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { formatNumber } from '../lib/format'
import { IconClose } from './icons'

export interface SelectionBarProps {
  /** How many rows are picked, across every page. */
  count: number
  onClear: () => void
  /** The bulk actions, as `sm` buttons. */
  actions?: ReactNode
  className?: string
}

export function SelectionBar({ count, onClear, actions, className }: SelectionBarProps) {
  return (
    <div className={cn('flex min-w-0 flex-1 flex-wrap items-center gap-3', className)}>
      <p className="flex shrink-0 items-center gap-2 text-base font-semibold text-ink" aria-live="polite">
        Selected:
        {/*
          Its own chip rather than a Badge: Badge wraps its children in an
          inline truncating span, and a grid button inside an inline span is a
          block, so the cross dropped onto a line of its own under the count.
        */}
        <span className="inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-neutral-soft-border bg-neutral-soft pl-2 pr-1 text-xs font-medium text-neutral-text">
          <span className="tabular-nums">{formatNumber(count)}</span>
          {/*
            A real button inside the badge, named for what it does. The glyph
            alone says close, and close what is exactly the question.
          */}
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear selection"
            title="Clear selection"
            className="grid h-4 w-4 place-items-center rounded-full text-ink-subtle hover:bg-surface-hover-strong hover:text-ink-secondary focus-visible:outline-none focus-visible:shadow-focus"
          >
            <IconClose className="h-3 w-3" />
          </button>
        </span>
      </p>
      {actions === undefined ? null : <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}
